import { useState } from "react";

import { useQuery, useMutation } from "@tanstack/react-query";

import { Room } from "../../../../../../../models/room";
import { Filters, Searches, Sorts, Sort } from "../../../../../../../models/shipeezi.table";
import RoomService from "../../../../../../../services/room.service";
import { useAppState } from "../../../../../../../context/StateContext";
import { NotificationToast } from "../../../../../../../components/Generic/Toast/NotificationToast";

const useActions = () => {
  const { currentUser } = useAppState();

  const [rooms, setRooms] = useState<Room[]>([]);
  const [hasErrors, setHasErrors] = useState<boolean>();

  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(20);
  const [total, setTotal] = useState<number>(0);
  const [filters, setFilters] = useState<Filters>();
  const [searches, setSearches] = useState<Searches>([]);
  const [sorting, setSorting] = useState<Sorts>();

  const { refetch, isFetching } = useQuery(
    ["room.list", page, pageSize, filters, searches, sorting],
    () =>
      RoomService.getAll({
        page,
        pageSize,
        jobId: "",
        companyId: currentUser?.companyId,
        sortBy: sorting
          ? JSON.stringify([...sorting, ["name", "asc"]])
          : JSON.stringify([["name", "asc"]]),
        searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
        ...filters,
      }),
    {
      enabled: !!currentUser?.id,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) setRooms(response.data);
      },
    }
  );

  const { refetch: refetchRoomTotal } = useQuery(
    ["room.count.list", filters, searches],
    () =>
      RoomService.count({
        jobId: "",
        companyId: currentUser?.companyId,
        searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
        ...filters,
      }),
    {
      enabled: !!currentUser?.id,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) setTotal(response.data);
        else setTotal(0);
      },
    }
  );

  const { mutate: updateRoom, isLoading: isUpdating } = useMutation(
    ["room.update"],
    ({ id, room }: { id: number; room: Room }) => RoomService.update(id, room),
    {
      onSuccess: () => {
        setHasErrors(false);
        refetch();
        refetchRoomTotal();
      },
      onError: (err: any) => {
        setHasErrors(true);
        NotificationToast.error(err?.response?.data?.message);
      },
    }
  );

  const { mutate: createRoom, isLoading: isCreating } = useMutation(
    ["room.create"],
    (room: Room) => RoomService.create(room),
    {
      onSuccess: () => {
        setHasErrors(false);
        refetch();
        refetchRoomTotal();
      },
      onError: (err: any) => {
        setHasErrors(true);
        NotificationToast.error(err?.response?.data?.message);
      },
    }
  );

  const setCurrentPage = (numberPage: number) => setPage(numberPage - 1);

  const handleSearch = (value: Searches) => {
    setSearches(value);
    setPage(0);
  };

  const handleFilter = (value: Filters) => {
    setFilters(value);
    setPage(0);
  };

  const handleSort = (sorts: Sort[]) => {
    const sorting: Sorts = [];

    sorts.forEach((sort) => sorting.push([sort.columnName, sort.direction]));
    setSorting(sorting);
  };

  return {
    page,
    pageSize,
    total,
    hasErrors,
    rooms,
    isLoading: isFetching || isCreating || isUpdating,
    setHasErrors,
    setPageSize,
    setCurrentPage,
    handleSearch,
    handleFilter,
    handleSort,
    createRoom,
    updateRoom,
  };
};

export default useActions;
